import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from 'antd';
import { Search, Package, Check, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchProductCatalog, searchProductCatalog } from '@/api/productCatalogService';
import { ProductCatalogItem } from '@/types/productCatalog';
import { BasicProduct } from '@/types';

interface ProductCatalogModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (products: BasicProduct[]) => void;
}

const MIN_SEARCH_LENGTH = 3;

const ProductCatalogModal: React.FC<ProductCatalogModalProps> = ({
  visible,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ProductCatalogItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();

  // Load 10 recent products when modal opens
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    const loadRecent = async () => {
      setLoading(true);
      try {
        const res = await fetchProductCatalog(1, 10);
        if (!cancelled) {
          setResults(res?.object?.items ?? []);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadRecent();
    return () => { cancelled = true; };
  }, [visible]);

  const handleSearch = useCallback((text: string) => {
    setSearch(text);
    if (searchTimer.current) clearTimeout(searchTimer.current);

    // If cleared, reload recent products
    if (!text.trim()) {
      const loadRecent = async () => {
        setLoading(true);
        try {
          const res = await fetchProductCatalog(1, 10);
          setResults(res?.object?.items ?? []);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      };
      loadRecent();
      return;
    }

    // Only search after MIN_SEARCH_LENGTH characters
    if (text.trim().length < MIN_SEARCH_LENGTH) return;

    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchProductCatalog(text);
        const items = Array.isArray(res?.object) ? res.object : [];
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  const toggleSelect = useCallback((uid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const selectedProducts: BasicProduct[] = results
      .filter((item) => selected.has(item.uid))
      .map((item) => ({
        title: item.title,
        price: item.price !== null ? String(item.price) : '',
        quantity: String(item.quantity ?? 1),
        product_catalog_uid: item.uid,
        is_fragile: item.is_fragile,
      }));

    onSelect(selectedProducts);
    handleClose();
  }, [results, selected, onSelect]);

  const handleClose = useCallback(() => {
    setSearch('');
    setResults([]);
    setSelected(new Set());
    onClose();
  }, [onClose]);

  const selectedCount = selected.size;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      title={null}
      closable={false}
      width={480}
      centered
      className='sd-scope sd-font'
      styles={{ body: { padding: 0 } }}
    >
      {/* Header */}
      <div className='px-5 pt-5 pb-3 border-b border-sd-border'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-semibold text-sd-foreground m-0'>
            {String(t('select_from_catalog', { defaultValue: 'Select from Catalog' }))}
          </h3>
          <button
            type='button'
            onClick={handleClose}
            className='text-sd-muted-foreground hover:text-sd-foreground text-lg leading-none cursor-pointer bg-transparent border-0 p-1'
          >
            ×
          </button>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sd-muted-foreground pointer-events-none' />
          <input
            type='text'
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={String(t('search_products', { defaultValue: 'Search products...' }))}
            className='w-full h-9 ps-9 pe-3 rounded-lg border border-sd-input bg-sd-input-bg text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring'
            autoFocus
          />
          {search.length > 0 && search.length < MIN_SEARCH_LENGTH && (
            <span className='text-[11px] text-sd-muted-foreground mt-1 block'>
              {String(t('min_search_characters', { defaultValue: 'Type at least 3 characters to search' }))}
            </span>
          )}
        </div>

        {/* Info: selected products will be merged into one item */}
        <div className='mt-2 px-2 py-1.5 rounded-md bg-sd-muted/50 text-[11px] text-sd-muted-foreground'>
          {String(t('catalog_merge_hint', { defaultValue: 'Selected products will be combined into one order item. Prices will be summed together.' }))}
        </div>
      </div>

      {/* Results */}
      <div className='max-h-[360px] overflow-y-auto'>
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='h-5 w-5 rounded-full border-2 border-sd-primary border-t-transparent animate-spin' />
          </div>
        ) : results.length > 0 ? (
          <div className='py-1'>
            {results.map((item) => {
              const isSelected = selected.has(item.uid);
              return (
                <button
                  key={item.uid}
                  type='button'
                  onClick={() => toggleSelect(item.uid)}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-start cursor-pointer transition-colors border-0 ${
                    isSelected
                      ? 'bg-[rgba(237,224,74,0.1)]'
                      : 'bg-transparent hover:bg-sd-muted/50'
                  }`}
                >
                  {/* Checkbox */}
                  <div
                    className={`rounded border flex items-center justify-center shrink-0 transition-colors ${
                      isSelected
                        ? 'bg-[rgb(237,224,74)] border-[rgb(237,224,74)]'
                        : 'border-sd-input bg-sd-background'
                    }`}
                    style={{ width: 18, height: 18 }}
                  >
                    {isSelected && <Check className='h-3 w-3 text-[rgb(57,57,60)]' />}
                  </div>

                  {/* Product info */}
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-1.5'>
                      <span className='text-[13px] font-medium text-sd-foreground truncate'>
                        {item.title}
                      </span>
                      {item.is_fragile && (
                        <ShieldAlert className='h-3 w-3 text-[rgb(217,119,6)] shrink-0' />
                      )}
                    </div>
                    {item.category && (
                      <span className='text-[11px] text-sd-muted-foreground'>{item.category}</span>
                    )}
                  </div>

                  {/* Price */}
                  <span className='text-[13px] font-medium text-sd-foreground shrink-0' dir='ltr'>
                    {item.price !== null
                      ? `${item.price.toLocaleString()} IQD`
                      : String(t('no_price_set', { defaultValue: 'No price set' }))}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center py-12 gap-2'>
            <Package className='h-8 w-8 text-sd-muted-foreground/40' />
            <span className='text-sm text-sd-muted-foreground'>
              {String(t('no_products_found', { defaultValue: 'No products found' }))}
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className='px-5 py-3 border-t border-sd-border flex items-center justify-between'>
        <span className='text-xs text-sd-muted-foreground'>
          {selectedCount > 0
            ? String(t('products_selected', { count: selectedCount, defaultValue: `${selectedCount} selected` }))
            : ''}
        </span>
        <div className='flex gap-2'>
          <button
            type='button'
            onClick={handleClose}
            className='h-8 px-4 rounded-lg border border-sd-input bg-sd-card text-[12px] font-medium cursor-pointer hover:bg-sd-muted transition-colors'
          >
            {String(t('cancel', { defaultValue: 'Cancel' }))}
          </button>
          <button
            type='button'
            onClick={handleConfirm}
            disabled={selectedCount === 0}
            className='h-8 px-4 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[12px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
          >
            {String(t('add_selected', { defaultValue: 'Add Selected' }))}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProductCatalogModal;
